import React, { Component } from 'react';
import { Card, CardContent, CircularProgress, Select, MenuItem, IconButton, Tooltip } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import ReactHighcharts from 'react-highcharts';
import { fetchUtils } from 'react-admin';

const fetch = fetchUtils.fetchJson;
const BASE_URL = window.nobid_endpoint + '/realtime_report?';
const BORDER_COLOR = '#666666';

const IMPRESSIONS_COLOR = '#009fdb';
const REVENUE_COLOR = '#49b27b';
const TODAY_COLOR = 'yellow';

const METRIC_CHOICES = [
  { id: 'CP', name: '$Revenue', prefix: '$', suffix: ' USD' },
  { id: 'IM', name: '#Impressions', prefix: '', suffix: '' },
];

const INTERVAL_CHOICES = [
  { id: 'm', name: 'Minutes' },
  { id: 'h', name: 'Hours' },
];

const AUTO_REFRESH_MS = 5 * 60 * 1000; // 5 minutes

// Filter keys from the parent DashboardChart that the realtime backend supports
const REALTIME_FILTER_KEYS = ['publisher_id', 'site_id', 'bidder_id', 'device', 'country'];

function extractFilters(parentParams) {
  if (!parentParams || !Array.isArray(parentParams)) return '';
  const filterParams = parentParams.filter(p =>
    REALTIME_FILTER_KEYS.some(key => p.startsWith(key + '='))
  );
  return filterParams.length > 0 ? '&' + filterParams.join('&') : '';
}

function roundVal(v, decimals = 2) {
  if (v === undefined || v === null || isNaN(v)) return 0;
  return parseFloat(Number(v).toFixed(decimals));
}

function getDay(dateStr) {
  const date = new Date(dateStr);
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return '' + y + '-' + (m <= 9 ? '0' + m : m) + '-' + (d <= 9 ? '0' + d : d);
}

function getTime(dateStr) {
  const date = new Date(dateStr);
  const h = date.getHours();
  const m = date.getMinutes();
  const s = date.getSeconds();
  return '' + (h <= 9 ? '0' + h : h) + ':' + (m <= 9 ? '0' + m : m) + ':' + (s <= 9 ? '0' + s : s);
}

/**
 * Separate raw datarows into: previous days, today, and today-over-yesterday overlay.
 * Returns { previousDays: [{x,y}...], today: [{x,y}...], todayOverlay: [{x,y}...] }
 */
function processSeriesData(datarows) {
  if (!datarows || datarows.length === 0) return { previousDays: [], today: [], todayOverlay: [] };

  const lastDate = datarows[datarows.length - 1][0];
  const todaysDay = getDay(lastDate);
  const todayPoints = [];
  const previousDaysPoints = [];
  const todayEntries = []; // keep raw date+value for overlay matching

  for (let i = 0; i < datarows.length; i++) {
    const date = datarows[i][0];
    const value = roundVal(Number(datarows[i][1]));
    const day = getDay(date);
    const ts = new Date(date).getTime();

    if (day === todaysDay) {
      // Bridge the gap: duplicate the last previous-day point as the first today point
      if (todayPoints.length === 0 && previousDaysPoints.length > 0) {
        const lastPrev = previousDaysPoints[previousDaysPoints.length - 1];
        todayPoints.push({ x: lastPrev.x, y: lastPrev.y });
      }
      todayPoints.push({ x: ts, y: value });
      todayEntries.push({ date, value, time: getTime(date) });
    } else {
      previousDaysPoints.push({ x: ts, y: value });
    }
  }

  // Bridge the gap: duplicate the first today point as the last previous-day point
  if (todayPoints.length > 0 && previousDaysPoints.length > 0) {
    const firstToday = todayPoints.find(p => getDay(new Date(p.x).toISOString()) === todaysDay);
    if (firstToday) {
      previousDaysPoints.push({ x: firstToday.x, y: firstToday.y });
    }
  }

  // Build today-over-yesterday overlay: for each previous day's point,
  // if today has a matching time, plot today's value at the previous day's date
  const todayTimeMap = new Map();
  for (const entry of todayEntries) {
    if (!todayTimeMap.has(entry.time)) {
      todayTimeMap.set(entry.time, entry.value);
    }
  }

  const todayOverlayPoints = [];
  for (let i = 0; i < datarows.length; i++) {
    const date = datarows[i][0];
    const day = getDay(date);
    if (day === todaysDay) continue;
    const time = getTime(date);
    if (todayTimeMap.has(time)) {
      const ts = new Date(date).getTime();
      todayOverlayPoints.push({ x: ts, y: todayTimeMap.get(time) });
    }
  }

  return {
    previousDays: previousDaysPoints,
    today: todayPoints,
    todayOverlay: todayOverlayPoints,
  };
}

/**
 * Calculate trend by comparing today's completed intervals vs yesterday's same intervals.
 * Excludes the current (incomplete) interval to avoid a misleading downward bias.
 * Returns { direction: 'up'|'down'|'flat', pct: number }
 */
function calcTrend(datarows) {
  if (!datarows || datarows.length < 3) return { direction: 'flat', pct: 0 };

  const lastDate = datarows[datarows.length - 1][0];
  const todaysDay = getDay(lastDate);

  // Group values by day
  const byDay = {};
  for (const row of datarows) {
    const day = getDay(row[0]);
    const time = getTime(row[0]);
    const value = Number(row[1]);
    if (!byDay[day]) byDay[day] = {};
    byDay[day][time] = value;
  }

  const days = Object.keys(byDay).sort();
  if (days.length < 2) return { direction: 'flat', pct: 0 };

  const yesterday = days[days.length - 2];
  const todayTimes = Object.keys(byDay[todaysDay] || {}).sort();

  // Exclude the last (current/incomplete) interval
  const completedTimes = todayTimes.slice(0, -1);
  if (completedTimes.length === 0) return { direction: 'flat', pct: 0 };

  let todaySum = 0;
  let yesterdaySum = 0;
  let matchCount = 0;
  for (const time of completedTimes) {
    todaySum += byDay[todaysDay][time] || 0;
    if (byDay[yesterday] && byDay[yesterday][time] !== undefined) {
      yesterdaySum += byDay[yesterday][time];
      matchCount++;
    }
  }

  if (matchCount === 0 || yesterdaySum === 0) return { direction: 'flat', pct: 0 };

  const pct = ((todaySum - yesterdaySum) / yesterdaySum) * 100;
  const direction = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  return { direction, pct: Math.abs(roundVal(pct, 1)) };
}

function buildChartConfig(seriesData, metricName, color, loading, interval) {
  const series = [];
  if (!loading) {
    // Previous days - main area
    series.push({
      name: metricName,
      type: 'area',
      color: color,
      fillOpacity: 0.15,
      lineWidth: 1.5,
      shadow: false,
      data: seriesData.previousDays,
      marker: { enabled: false },
      zIndex: 1,
    });
    // Today - yellow area on top
    series.push({
      name: metricName + ' Now',
      type: 'area',
      color: TODAY_COLOR,
      fillColor: 'rgba(255, 255, 0, 0.15)',
      lineWidth: 2,
      shadow: false,
      data: seriesData.today,
      marker: { enabled: false },
      zIndex: 3,
      dataLabels: interval === 'h' ? { enabled: true, color: 'white', style: { textOutline: 'none', fontSize: '9px' } } : { enabled: false },
    });
    // Today overlay on previous days - yellow scatter
    if (seriesData.todayOverlay.length > 0) {
      series.push({
        name: 'Today',
        type: 'scatter',
        color: TODAY_COLOR,
        data: seriesData.todayOverlay,
        marker: { radius: 3, symbol: 'circle' },
        zIndex: 7,
        dataLabels: interval === 'h' ? { enabled: true, color: 'white', style: { textOutline: 'none', fontSize: '9px' } } : { enabled: false },
      });
    }
  }

  return {
    chart: {
      renderTo: 'container',
      zoomType: 'x',
      height: 200,
      backgroundColor: 'none',
      borderColor: BORDER_COLOR,
      borderWidth: 0,
      spacing: [5, 10, 5, 10],
    },
    time: { useUTC: false },
    title: { text: metricName, style: { color: '#ffffff', fontFamily: 'Roboto, Helvetica, Arial, sans-serif', fontSize: '11px' }, margin: 2 },
    subtitle: { text: '' },
    xAxis: {
      type: 'datetime',
      labels: { format: '{value:%e-%b %H:%M}', style: { color: '#ffffff', fontFamily: 'Roboto, Helvetica, Arial, sans-serif' } },
      crosshair: true,
    },
    yAxis: [{
      labels: { style: { color: '#ffffff', fontFamily: 'Roboto, Helvetica, Arial, sans-serif' } },
      title: { text: '', style: { color: '#ffffff', fontWeight: 'bold', fontFamily: 'Roboto, Helvetica, Arial, sans-serif' } },
      min: 0,
      gridLineWidth: 1,
      gridLineColor: '#555555',
    }],
    series: series,
    tooltip: { crosshairs: true, shared: true },
    legend: {
      enabled: true,
      itemStyle: { color: '#ffffff', fontFamily: 'Roboto, Helvetica, Arial, sans-serif', fontSize: '10px' },
    },
    credits: { enabled: false },
    plotOptions: { series: { turboThreshold: 10000 } },
  };
}

export default class RealtimeSummaryComponent extends Component {
  state = {
    loading: true,
    datarows: [],
    selectedMetric: 'CP',
    selectedInterval: 'h',
    lastUpdated: null,
  };

  timer = null;

  componentDidMount() {
    this.fetchSelectedMetric();
    this.timer = setInterval(() => this.fetchSelectedMetric(), AUTO_REFRESH_MS);
  }

  componentDidUpdate(prevProps) {
    if (this.props.submit && !prevProps.submit) {
      this.fetchSelectedMetric();
    }
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  fetchSelectedMetric() {
    this.setState({ loading: true });
    const metricType = this.state.selectedMetric;
    const fixedParams = {
      type: metricType,
      interval: this.state.selectedInterval,
      time_range: 48,
    };
    const queryString = new URLSearchParams(fixedParams);
    const filters = extractFilters(this.props.params);
    const url = `${BASE_URL}${queryString}${filters}`;
    let options = { headers: new Headers() };
    options.headers.set('accept', 'application/json');
    const token = localStorage.getItem('token');
    options.headers.set('Authorization', `Bearer ${token}`);
    fetch(url, options).then(response => {
      const results = JSON.parse(response.body);
      const datarows = (results && results.schema && results.datarows) ? results.datarows : [];
      this.setState({
        datarows,
        loading: false,
        lastUpdated: new Date(),
      });
    }).catch(() => {
      this.setState({ loading: false });
    });
  }

  handleMetricChange = (event) => {
    this.setState({ selectedMetric: event.target.value }, () => {
      this.fetchSelectedMetric();
    });
  }

  handleIntervalChange = (event) => {
    this.setState({ selectedInterval: event.target.value }, () => {
      this.fetchSelectedMetric();
    });
  }

  handleRefresh = () => {
    this.fetchSelectedMetric();
  }

  render() {
    const { loading, datarows, selectedMetric, selectedInterval, lastUpdated } = this.state;

    const seriesData = processSeriesData(datarows);
    const chartColor = selectedMetric === 'CP' ? REVENUE_COLOR : IMPRESSIONS_COLOR;
    const chartMetric = METRIC_CHOICES.find(m => m.id === selectedMetric);
    const chartConfig = buildChartConfig(seriesData, chartMetric.name, chartColor, loading, selectedInterval);

    const trend = calcTrend(datarows);
    const trendColor = trend.direction === 'up' ? '#49b27b' : trend.direction === 'down' ? '#f02d5b' : '#aaa';
    const trendArrow = trend.direction === 'up' ? '\u25B2' : trend.direction === 'down' ? '\u25BC' : '\u25CF';

    const timeLabel = lastUpdated ? lastUpdated.toLocaleTimeString() : '';

    return (
      <div style={{ width: '100%', paddingBottom: '5px' }}>
        <Card style={{ padding: '0' }} raised>
          <CardContent style={{ padding: '8px 12px', '&:last-child': { paddingBottom: '8px' } }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
                REALTIME <span style={{ fontWeight: 'normal', fontSize: '11px', color: '#aaa' }}>Last 2 Days &middot; {INTERVAL_CHOICES.find(c => c.id === selectedInterval).name} Intervals</span>
                {!loading && trend.direction !== 'flat' && (
                  <Tooltip title={`Today vs yesterday (completed intervals): ${trend.direction === 'up' ? '+' : '-'}${trend.pct}%`}>
                    <span style={{ color: trendColor, fontSize: '13px', fontWeight: 'bold', marginLeft: '8px' }}>
                      {trendArrow} {trend.pct}%
                    </span>
                  </Tooltip>
                )}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ color: '#aaa', fontSize: '11px' }}>Auto-refresh every 5 min</span>
                {lastUpdated && <span style={{ color: '#aaa', fontSize: '11px' }}>&middot; Updated {timeLabel}</span>}
                <Tooltip title="Refresh now">
                  <IconButton size="small" onClick={this.handleRefresh} disabled={loading} style={{ color: 'white' }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            {loading && datarows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <CircularProgress />
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2px' }}>
                  <Select
                    value={selectedMetric}
                    onChange={this.handleMetricChange}
                    style={{ color: 'white', fontSize: '12px' }}
                    disableUnderline
                  >
                    {METRIC_CHOICES.map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                  <Select
                    value={selectedInterval}
                    onChange={this.handleIntervalChange}
                    style={{ color: 'white', fontSize: '12px' }}
                    disableUnderline
                    disabled={loading}
                  >
                    {INTERVAL_CHOICES.map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                  <span style={{ fontSize: '11px', color: '#aaa', marginLeft: 'auto' }}>Local timezone</span>
                </div>

                <ReactHighcharts config={chartConfig} isPureConfig={true} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
}
